import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { handleAPIError, TOAST_MESSAGES } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import { analytics } from '@/services/analytics';
import { setUserIDProvider } from '@/services/errorReporter';
import type { User } from '@/models/user';

interface UserContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;    // = (permissionGroup === 'ADMIN')
  isEmployee: boolean; // = (permissionGroup === 'EMPLOYEE')
  isStaff: boolean;    // = (isAdmin || isEmployee)
  refetchUser: () => Promise<void>;
  updateUser: (updatedUser: User) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const fetchUser = async (force = false) => {
    if (!APIRoutes.isAuthenticated()) {
      setLoading(false);
      setHasInitialized(true);
      return;
    }

    // Don't re-fetch if we already have user data and this isn't a forced refresh
    if (!force && hasInitialized && user !== null) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const userData = await APIRoutes.getCurrentUser();
      setUser(userData);
      setHasInitialized(true);

      // Identify user in analytics
      analytics.identify(userData.id, {
        $email: userData.email,
        $name: `${userData.firstName} ${userData.lastName}`,
        firstName: userData.firstName,
        lastName: userData.lastName,
        permissionGroup: userData.permissionGroup,
        isCommunityMember: userData.featureConfig.communityMember,
        activeSubscriptionTypes: userData.activeSubscriptionTypes,
        activeSubscriptionProductTypes: userData.activeSubscriptionProductTypes,
        subscriptionCount: userData.activeSubscriptionTypes.length,
      });
    } catch (err) {
      setError('Fehler beim Laden der Benutzerdaten');
      handleAPIError(err, TOAST_MESSAGES.USER_LOAD_ERROR);
      setUser(null);
      setHasInitialized(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []); // Only run on component mount

  useEffect(() => {
    setUserIDProvider(() => user?.id ?? null);
    return () => {
      setUserIDProvider(null);
    };
  }, [user]);

  const isAdmin = user?.permissionGroup === 'ADMIN';
  const isEmployee = user?.permissionGroup === 'EMPLOYEE';
  const isStaff = isAdmin || isEmployee;

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const value: UserContextType = {
    user,
    loading,
    error,
    isAdmin,
    isEmployee,
    isStaff,
    refetchUser: () => fetchUser(true),
    updateUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}